import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Alert, Box, Button, Card, CardContent, Chip, Divider, FormControlLabel,
  Grid, IconButton, MenuItem, Stack, Switch, Tab, Tabs, TextField, Typography,
} from '@mui/material';
import { Add, ArrowBack, AutoFixHigh, Delete } from '@mui/icons-material';
import PageShell from '../../components/layout/PageShell';
import PageHeader from '../../components/layout/PageHeader';
import FormFileUpload from '../../components/ui/forms/FormFileUpload';
import { getRestaurants, getRestaurantMenu, createMenuItem, updateMenuItem } from '../../services/businessService';
import { getMasters } from '../../services/platform/masterService';
import {
  DAYS, FOOD_WIZARD_TABS, emptyFoodWizard, generateVariantsFromAttributes,
  menuItemToWizard, wizardToMenuPayload, type FoodWizardState,
} from './foodWizardTypes';
import { matchesParentMaster } from '../../constants/masterRefFields';
import { resolveFoodThumbnailFromMasters } from '../../utils/foodMasterImages';
import { chargeFieldLabel, formatMoney } from '../../utils/currency';
import { resolveFoodTaxGroupDisplayName } from '../../constants/foodMasterRules';
import { resolveMasterRecordActive } from '../../constants/transportSubserviceMasters';

type MasterRow = {
  _id: string;
  code: string;
  name: string;
  isActive?: boolean;
  parentId?: string | { _id?: string };
  metadata?: Record<string, unknown>;
};

function activeMasterOptions(options: MasterRow[], currentValue = ''): MasterRow[] {
  const active = options.filter((row) => resolveMasterRecordActive(row));
  if (currentValue && !active.some((row) => row.code === currentValue)) {
    const selected = options.find((row) => row.code === currentValue);
    if (selected) return [...active, selected];
  }
  return active;
}

const FOOD_ADD_MASTER_TYPES = [
  'restaurant_types', 'restaurant_branches', 'food_categories', 'food_sub_categories', 'cuisine_types',
  'dietary_types', 'food_tags', 'food_attributes', 'food_attribute_values',
  'addon_groups', 'food_addons', 'preparation_times', 'food_units',
  'food_tax_groups', 'kitchen_stations', 'food_availability_schedules', 'food_images',
] as const;

async function loadMasters(type: string): Promise<MasterRow[]> {
  const res = await getMasters(type, { limit: 300 });
  return (res.data || []) as MasterRow[];
}

function MasterSelect({
  label, value, onChange, options, required, helperText, renderLabel,
}: {
  label: string; value: string; onChange: (v: string) => void;
  options: MasterRow[]; required?: boolean; helperText?: string;
  renderLabel?: (row: MasterRow) => string;
}) {
  return (
    <TextField select fullWidth label={label} value={value} onChange={(e) => onChange(e.target.value)}
      required={required} helperText={helperText}>
      <MenuItem value="">— Select —</MenuItem>
      {options.map((o) => (
        <MenuItem key={o._id} value={o.code}>{renderLabel ? renderLabel(o) : o.name}</MenuItem>
      ))}
    </TextField>
  );
}

export default function FoodAddWizardPage() {
  const navigate = useNavigate();
  const { menuItemId } = useParams<{ menuItemId?: string }>();
  const [searchParams] = useSearchParams();
  const isEdit = !!menuItemId;
  const [tab, setTab] = useState(0);
  const [form, setForm] = useState<FoodWizardState>(() => emptyFoodWizard(searchParams.get('restaurantId') || ''));

  const patch = (partial: Partial<FoodWizardState>) => setForm((f) => ({ ...f, ...partial }));

  const { data: restaurants = [] } = useQuery({
    queryKey: ['restaurants-wizard'],
    queryFn: () => getRestaurants(true),
  });

  const { data: menu = [] } = useQuery({
    queryKey: ['menu-wizard', form.restaurantId],
    queryFn: () => getRestaurantMenu(form.restaurantId, true),
    enabled: !!form.restaurantId,
  });

  useEffect(() => {
    if (!isEdit || !menuItemId || !menu.length) return;
    const item = menu.find((m: { _id: string }) => m._id === menuItemId);
    if (item) setForm(menuItemToWizard(item as Record<string, unknown>, form.restaurantId));
  }, [isEdit, menuItemId, menu, form.restaurantId]);

  const mastersQuery = useQuery({
    queryKey: ['food-add-masters'],
    queryFn: async () => {
      const entries = await Promise.all(FOOD_ADD_MASTER_TYPES.map(async (t) => [t, await loadMasters(t)] as const));
      return Object.fromEntries(entries) as Record<string, MasterRow[]>;
    },
  });

  const m = mastersQuery.data || {};

  useEffect(() => {
    if (isEdit || !mastersQuery.data) return;
    if (!form.thumbnailImage) {
      const url = resolveFoodThumbnailFromMasters({
        foodCode: form.foodCode,
        categoryCode: form.categoryId,
        cuisineCode: form.cuisineId,
        foodImages: m.food_images || [],
        categories: m.food_categories || [],
        cuisines: m.cuisine_types || [],
      });
      if (url) patch({ thumbnailImage: url });
    }
    if (form.foodCode && !form.galleryImages.some(Boolean)) {
      const gallery = (m.food_images || [])
        .filter((row) => String(row.metadata?.foodCode || '') === form.foodCode && row.metadata?.imageType === 'gallery')
        .map((row) => String(row.metadata?.imageUrl || ''))
        .filter(Boolean);
      if (gallery.length) patch({ galleryImages: gallery });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.foodCode, form.categoryId, form.cuisineId, mastersQuery.data, isEdit]);

  const subCategories = useMemo(() => {
    const cat = (m.food_categories || []).find((c) => c.code === form.categoryId);
    return (m.food_sub_categories || []).filter(
      (s) => matchesParentMaster(s, form.categoryId, cat ? { _id: cat._id } : undefined, 'parentCategoryCode'),
    );
  }, [m.food_sub_categories, m.food_categories, form.categoryId]);

  const attributeValues = useMemo(() => {
    const map: Record<string, MasterRow[]> = {};
    for (const attr of m.food_attributes || []) {
      map[attr.code] = (m.food_attribute_values || []).filter(
        (v) => matchesParentMaster(v, attr.code, { _id: attr._id }, 'attributeCode'),
      );
    }
    return map;
  }, [m.food_attributes, m.food_attribute_values]);

  const valueNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const v of m.food_attribute_values || []) map[v.code] = v.name;
    return map;
  }, [m.food_attribute_values]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = wizardToMenuPayload(form);
      if (isEdit && menuItemId) return updateMenuItem(form.restaurantId, menuItemId, payload);
      return createMenuItem(form.restaurantId, payload);
    },
    onSuccess: () => navigate(`/food/menu?restaurantId=${form.restaurantId}`),
  });

  const tabValid = (t: number) => {
    if (t === 0) return !!form.restaurantId && !!form.foodName.trim() && !!form.categoryId;
    if (t === 1) return form.basePrice > 0 || form.variants.some((v) => v.price > 0);
    return true;
  };

  const renderTab = () => {
    switch (tab) {
      case 0:
        return (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth label="Restaurant" value={form.restaurantId}
                onChange={(e) => patch({ restaurantId: e.target.value })} required>
                {restaurants.map((r: { _id: string; name: string }) => (
                  <MenuItem key={r._id} value={r._id}>{r.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <MasterSelect label="Branch" value={form.branchId} onChange={(v) => patch({ branchId: v })}
                options={m.restaurant_branches || []} helperText="Optional — multi-branch SaaS" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Food Name" value={form.foodName}
                onChange={(e) => patch({ foodName: e.target.value })} required />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField fullWidth label="Food Code" value={form.foodCode}
                onChange={(e) => patch({ foodCode: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField fullWidth label="SKU" value={form.sku} onChange={(e) => patch({ sku: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <MasterSelect label="Category" value={form.categoryId} onChange={(v) => patch({ categoryId: v, subCategoryId: '' })}
                options={m.food_categories || []} required />
            </Grid>
            <Grid item xs={12} sm={6}>
              <MasterSelect label="Sub Category" value={form.subCategoryId} onChange={(v) => patch({ subCategoryId: v })}
                options={subCategories} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <MasterSelect label="Cuisine" value={form.cuisineId} onChange={(v) => patch({ cuisineId: v })}
                options={m.cuisine_types || []} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <MasterSelect label="Dietary Type" value={form.dietaryTypeId} onChange={(v) => patch({ dietaryTypeId: v })}
                options={m.dietary_types || []} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <MasterSelect label="Unit" value={form.unitId} onChange={(v) => patch({ unitId: v })}
                options={m.food_units || []} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Short Description" value={form.shortDescription}
                onChange={(e) => patch({ shortDescription: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline minRows={2} label="Description" value={form.description}
                onChange={(e) => patch({ description: e.target.value })} />
            </Grid>
          </Grid>
        );

      case 1:
        return (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth type="number" label={chargeFieldLabel('Base Price')} value={form.basePrice}
                onChange={(e) => patch({ basePrice: Number(e.target.value) })} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <MasterSelect label="Tax Group" value={form.taxGroupId} onChange={(v) => patch({ taxGroupId: v })}
                options={activeMasterOptions(m.food_tax_groups || [], form.taxGroupId)}
                renderLabel={(row) => (
                  row.code.startsWith('gst_') || Number.isFinite(Number(row.metadata?.taxPercent))
                    ? resolveFoodTaxGroupDisplayName(row)
                    : row.name
                )} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <MasterSelect label="Kitchen Station" value={form.kitchenStationId}
                onChange={(v) => patch({ kitchenStationId: v })}
                options={activeMasterOptions(m.kitchen_stations || [], form.kitchenStationId)} />
            </Grid>
            <Grid item xs={12}><Divider>Attribute-based variants</Divider></Grid>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Select attributes (Size, Crust, Spice…) then values to auto-generate variants.
              </Typography>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mb: 2 }}>
                {(m.food_attributes || []).map((attr) => {
                  const on = form.selectedAttributeCodes.includes(attr.code);
                  return (
                    <Chip key={attr._id} label={attr.name} color={on ? 'primary' : 'default'}
                      variant={on ? 'filled' : 'outlined'}
                      onClick={() => patch({
                        selectedAttributeCodes: on
                          ? form.selectedAttributeCodes.filter((c) => c !== attr.code)
                          : [...form.selectedAttributeCodes, attr.code],
                      })}
                    />
                  );
                })}
              </Stack>
              {form.selectedAttributeCodes.map((code) => (
                <Box key={code} sx={{ mb: 2 }}>
                  <Typography variant="caption" fontWeight={600}>{code}</Typography>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.5 }}>
                    {(attributeValues[code] || []).map((val) => {
                      const sel = (form.attributeValueSelections[code] || []).includes(val.code);
                      return (
                        <Chip key={val._id} size="small" label={val.name}
                          color={sel ? 'secondary' : 'default'} variant={sel ? 'filled' : 'outlined'}
                          onClick={() => {
                            const cur = form.attributeValueSelections[code] || [];
                            const next = sel ? cur.filter((c) => c !== val.code) : [...cur, val.code];
                            patch({ attributeValueSelections: { ...form.attributeValueSelections, [code]: next } });
                          }}
                        />
                      );
                    })}
                  </Stack>
                </Box>
              ))}
              <Button size="small" startIcon={<AutoFixHigh />} sx={{ mb: 2 }}
                onClick={() => patch({ variants: generateVariantsFromAttributes(form, {}, valueNameMap) })}>
                Generate Variants
              </Button>
            </Grid>
            {form.variants.map((v, i) => (
              <Grid container spacing={2} key={i} item xs={12}>
                <Grid item xs={12} sm={3}>
                  <TextField fullWidth label="Variant Name" value={v.name} onChange={(e) => {
                    const variants = [...form.variants];
                    variants[i] = { ...v, name: e.target.value };
                    patch({ variants });
                  }} />
                </Grid>
                <Grid item xs={6} sm={2}>
                  <TextField fullWidth label="SKU" value={v.sku} onChange={(e) => {
                    const variants = [...form.variants];
                    variants[i] = { ...v, sku: e.target.value };
                    patch({ variants });
                  }} />
                </Grid>
                <Grid item xs={6} sm={2}>
                  <TextField fullWidth type="number" label="Price" value={v.price} onChange={(e) => {
                    const variants = [...form.variants];
                    variants[i] = { ...v, price: Number(e.target.value) };
                    patch({ variants });
                  }} />
                </Grid>
                <Grid item xs={6} sm={2}>
                  <TextField fullWidth type="number" label="Cost" value={v.costPrice} onChange={(e) => {
                    const variants = [...form.variants];
                    variants[i] = { ...v, costPrice: Number(e.target.value) };
                    patch({ variants });
                  }} />
                </Grid>
                <Grid item xs={6} sm={2} display="flex" alignItems="center">
                  <IconButton color="error" onClick={() => patch({ variants: form.variants.filter((_, j) => j !== i) })}>
                    <Delete />
                  </IconButton>
                </Grid>
              </Grid>
            ))}
            <Grid item xs={12}>
              <Button startIcon={<Add />} onClick={() => patch({
                variants: [...form.variants, { name: '', sku: '', price: form.basePrice, costPrice: 0, attributeValues: {}, status: 'active' }],
              })}>Add Variant Manually</Button>
            </Grid>
          </Grid>
        );

      case 2:
        return (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Link addon groups and pick addons from masters.
            </Typography>
            {(m.addon_groups || []).map((group) => {
              const row = form.addonGroups.find((g) => g.groupCode === group.code);
              const addons = (m.food_addons || []).filter((a) => a.metadata?.addonGroupCode === group.code);
              return (
                <Card key={group._id} variant="outlined" sx={{ mb: 2 }}>
                  <CardContent>
                    <FormControlLabel
                      control={<Switch checked={!!row} onChange={(e) => {
                        if (e.target.checked) {
                          patch({ addonGroups: [...form.addonGroups, { groupCode: group.code, addonCodes: [] }] });
                        } else {
                          patch({ addonGroups: form.addonGroups.filter((g) => g.groupCode !== group.code) });
                        }
                      }} />}
                      label={`${group.name} (min ${group.metadata?.minSelection ?? 0}, max ${group.metadata?.maxSelection ?? '∞'})`}
                    />
                    {row && (
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 1 }}>
                        {addons.map((a) => {
                          const on = row.addonCodes.includes(a.code);
                          return (
                            <Chip key={a._id} label={`${a.name} — ${formatMoney(a.metadata?.price ?? 0)}`}
                              color={on ? 'primary' : 'default'} variant={on ? 'filled' : 'outlined'}
                              onClick={() => {
                                const groups = form.addonGroups.map((g) => {
                                  if (g.groupCode !== group.code) return g;
                                  const codes = on ? g.addonCodes.filter((c) => c !== a.code) : [...g.addonCodes, a.code];
                                  return { ...g, addonCodes: codes };
                                });
                                patch({ addonGroups: groups });
                              }}
                            />
                          );
                        })}
                      </Stack>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        );

      case 3:
        return (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <MasterSelect label="Preparation Time" value={form.preparationTimeId}
                onChange={(v) => patch({ preparationTimeId: v })} options={m.preparation_times || []} />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel control={<Switch checked={form.scheduleAvailability}
                onChange={(e) => patch({ scheduleAvailability: e.target.checked })} />}
                label="Use custom availability schedule" />
            </Grid>
            {form.scheduleAvailability && form.availabilitySchedule.map((row, i) => (
              <Grid container spacing={2} key={i} item xs={12}>
                <Grid item xs={12} sm={3}>
                  <TextField select fullWidth label="Day" value={row.day} onChange={(e) => {
                    const availabilitySchedule = [...form.availabilitySchedule];
                    availabilitySchedule[i] = { ...row, day: e.target.value };
                    patch({ availabilitySchedule });
                  }}>
                    {DAYS.map((d) => <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={5} sm={3}>
                  <TextField fullWidth label="Start" value={row.startTime} onChange={(e) => {
                    const availabilitySchedule = [...form.availabilitySchedule];
                    availabilitySchedule[i] = { ...row, startTime: e.target.value };
                    patch({ availabilitySchedule });
                  }} />
                </Grid>
                <Grid item xs={5} sm={3}>
                  <TextField fullWidth label="End" value={row.endTime} onChange={(e) => {
                    const availabilitySchedule = [...form.availabilitySchedule];
                    availabilitySchedule[i] = { ...row, endTime: e.target.value };
                    patch({ availabilitySchedule });
                  }} />
                </Grid>
                <Grid item xs={2} sm={1} display="flex" alignItems="center">
                  <IconButton color="error" onClick={() => patch({
                    availabilitySchedule: form.availabilitySchedule.filter((_, j) => j !== i),
                  })}><Delete /></IconButton>
                </Grid>
              </Grid>
            ))}
            {form.scheduleAvailability && (
              <Grid item xs={12}>
                <Button startIcon={<Add />} onClick={() => patch({
                  availabilitySchedule: [...form.availabilitySchedule, { day: 'mon', startTime: '09:00', endTime: '22:00' }],
                })}>Add Slot</Button>
              </Grid>
            )}
          </Grid>
        );

      case 4:
        return (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                Per-serving nutrition (optional). Shown to customers on the item detail sheet.
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Serving size" placeholder="e.g. 1 plate, 250g"
                value={form.nutrition.servingSize}
                onChange={(e) => patch({ nutrition: { ...form.nutrition, servingSize: e.target.value } })} />
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField fullWidth type="number" label="Calories (kcal)" value={form.nutrition.calories}
                onChange={(e) => patch({ nutrition: { ...form.nutrition, calories: e.target.value === '' ? '' : Number(e.target.value) } })} />
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField fullWidth type="number" label="Protein (g)" value={form.nutrition.protein}
                onChange={(e) => patch({ nutrition: { ...form.nutrition, protein: e.target.value === '' ? '' : Number(e.target.value) } })} />
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField fullWidth type="number" label="Carbohydrates (g)" value={form.nutrition.carbohydrates}
                onChange={(e) => patch({ nutrition: { ...form.nutrition, carbohydrates: e.target.value === '' ? '' : Number(e.target.value) } })} />
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField fullWidth type="number" label="Fat (g)" value={form.nutrition.fat}
                onChange={(e) => patch({ nutrition: { ...form.nutrition, fat: e.target.value === '' ? '' : Number(e.target.value) } })} />
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField fullWidth type="number" label="Fiber (g)" value={form.nutrition.fiber}
                onChange={(e) => patch({ nutrition: { ...form.nutrition, fiber: e.target.value === '' ? '' : Number(e.target.value) } })} />
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField fullWidth type="number" label="Sugar (g)" value={form.nutrition.sugar}
                onChange={(e) => patch({ nutrition: { ...form.nutrition, sugar: e.target.value === '' ? '' : Number(e.target.value) } })} />
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField fullWidth type="number" label="Sodium (mg)" value={form.nutrition.sodium}
                onChange={(e) => patch({ nutrition: { ...form.nutrition, sodium: e.target.value === '' ? '' : Number(e.target.value) } })} />
            </Grid>
          </Grid>
        );

      case 5:
        return (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1 }}>
                <Button size="small" variant="outlined" onClick={() => {
                  const url = resolveFoodThumbnailFromMasters({
                    foodCode: form.foodCode,
                    categoryCode: form.categoryId,
                    cuisineCode: form.cuisineId,
                    foodImages: m.food_images || [],
                    categories: m.food_categories || [],
                    cuisines: m.cuisine_types || [],
                  });
                  if (url) patch({ thumbnailImage: url });
                }}>
                  Load thumbnail from masters
                </Button>
                <Button size="small" variant="outlined" onClick={() => {
                  const gallery = (m.food_images || [])
                    .filter((row) => String(row.metadata?.foodCode || '') === form.foodCode && row.metadata?.imageType === 'gallery')
                    .map((row) => String(row.metadata?.imageUrl || ''))
                    .filter(Boolean);
                  if (gallery.length) patch({ galleryImages: gallery });
                }} disabled={!form.foodCode}>
                  Load gallery from food_images master
                </Button>
              </Stack>
            </Grid>
            <Grid item xs={12}>
              <FormFileUpload
                label="Thumbnail Image"
                value={form.thumbnailImage}
                onChange={(url) => patch({ thumbnailImage: url })}
                folder="food/menu/thumbnails"
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>Gallery Images</Typography>
              {form.galleryImages.map((url, i) => (
                <Stack key={i} direction="row" spacing={1} sx={{ mb: 1 }} alignItems="flex-start">
                  <Box flex={1}>
                    <FormFileUpload
                      label={`Gallery ${i + 1}`}
                      value={url}
                      onChange={(nextUrl) => {
                        const galleryImages = [...form.galleryImages];
                        galleryImages[i] = nextUrl;
                        patch({ galleryImages });
                      }}
                      folder="food/menu/gallery"
                    />
                  </Box>
                  {form.galleryImages.length > 1 && (
                    <IconButton onClick={() => patch({ galleryImages: form.galleryImages.filter((_, j) => j !== i) })}>
                      <Delete />
                    </IconButton>
                  )}
                </Stack>
              ))}
              <Button size="small" startIcon={<Add />} onClick={() => patch({ galleryImages: [...form.galleryImages, ''] })}>
                Add Image
              </Button>
            </Grid>
          </Grid>
        );

      case 6:
        return (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>Food Tags</Typography>
              <Stack direction="row" spacing={0.5} flexWrap="wrap">
                {(m.food_tags || []).map((t) => {
                  const on = form.tagIds.includes(t.code);
                  return (
                    <Chip key={t._id} label={t.name} color={on ? 'primary' : 'default'}
                      variant={on ? 'filled' : 'outlined'}
                      onClick={() => patch({
                        tagIds: on ? form.tagIds.filter((c) => c !== t.code) : [...form.tagIds, t.code],
                      })}
                    />
                  );
                })}
              </Stack>
            </Grid>
            <Grid item xs={12} display="flex" flexWrap="wrap" gap={2}>
              <FormControlLabel control={<Switch checked={form.isBestseller}
                onChange={(e) => patch({ isBestseller: e.target.checked })} />} label="Bestseller" />
              <FormControlLabel control={<Switch checked={form.isRecommended}
                onChange={(e) => patch({ isRecommended: e.target.checked })} />} label="Recommended" />
              <FormControlLabel control={<Switch checked={form.isFeatured}
                onChange={(e) => patch({ isFeatured: e.target.checked })} />} label="Featured" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth type="number" label="Display Order" value={form.displayOrder}
                onChange={(e) => patch({ displayOrder: Number(e.target.value) })} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField select fullWidth label="Status" value={form.status}
                onChange={(e) => patch({ status: e.target.value as 'active' | 'inactive' })}>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" fontWeight={700}>Summary</Typography>
                  <Typography variant="body2">{form.foodName} · {form.categoryId} · {formatMoney(form.basePrice)}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {form.variants.length} variants · {form.addonGroups.length} addon groups
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        );

      default:
        return null;
    }
  };

  return (
    <PageShell>
      <PageHeader
        eyebrow="Food Delivery"
        title={isEdit ? 'Edit Food Item' : 'Food Add'}
        subtitle="7-tab professional flow — only food-catalog masters (no inventory, recipe, or discount)"
        actions={<Button startIcon={<ArrowBack />} onClick={() => navigate('/food/menu')}>Back to Catalog</Button>}
      />

      {mastersQuery.isError && (
        <Alert severity="warning" sx={{ mb: 2 }}>Configure the 17 Food Add masters before creating items.</Alert>
      )}

      <Card>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto" sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
          {FOOD_WIZARD_TABS.map((t, i) => (
            <Tab key={t.title} label={t.title} disabled={i > 0 && !tabValid(0)} />
          ))}
        </Tabs>
        <CardContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{FOOD_WIZARD_TABS[tab].subtitle}</Typography>
          {renderTab()}
          <Stack direction="row" justifyContent="space-between" sx={{ mt: 3 }}>
            <Button disabled={tab === 0} onClick={() => setTab((t) => t - 1)}>Previous</Button>
            {tab < FOOD_WIZARD_TABS.length - 1 ? (
              <Button variant="contained" disabled={!tabValid(tab)} onClick={() => setTab((t) => t + 1)}>Next</Button>
            ) : (
              <Button variant="contained" color="success" disabled={!form.restaurantId || saveMutation.isPending}
                onClick={() => saveMutation.mutate()}>
                {isEdit ? 'Update Food' : 'Publish Food'}
              </Button>
            )}
          </Stack>
        </CardContent>
      </Card>
    </PageShell>
  );
}
